import styled from '@emotion/styled';
import { max, min } from 'lodash';
import React, { useState } from 'react';
import {
    getIntensityForStageAndDirection,
    isPrePostLudePathScore,
    TherapeuticDirection,
    Wavepath,
} from 'wavepaths-shared/core';
import { getDuration } from 'wavepaths-shared/sessionUtils';
import { formatDurationSeconds } from 'wavepaths-shared/util/dateUtils';

import { WaveCardDialog } from '@/components/WaveQueueEditor/WaveCardDialog';

import NowAndNextCard from './NowAndNextCard';

export const EditButton = styled.button({
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translate(-50%, -50%)',
    height: 30,
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0 12px',
    backgroundColor: '#2B2D3F',
    border: 'none',
    borderRadius: 15,
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: '#ffffff',
    cursor: 'pointer',
});

const AdjustmentsDivider = styled.div({
    height: 1,
    width: '100%',
    backgroundColor: '#2B2D3F',
    opacity: 0.2,
    mixBlendMode: 'color-burn',
});

const Adjustment = styled.div({
    width: '100%',
});

const AdjustmentItem = styled.div({
    height: 48,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '0 8px',
    margin: 0,
    border: 'none',
    textAlign: 'left',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: '#2B2D3F',
    backgroundColor: 'transparent',
});

const AdjustmentIcon = styled.svg({
    opacity: 0.5,
    mixBlendMode: 'color-burn',
});

const AdjustmentLabel = styled.div({});

export const SkipWaveButton = styled.button`
    position: absolute;
    top: 50%;
    left: 50%;
    background-color: transparent;
    border: none;
    display: grid;
    place-content: center;
    width: 80px;
    height: 80px;
    cursor: pointer;
    transform: translate(-50%, -50%) scale(0.8);
    @media (min-height: 760px) {
        transform: translate(-50%, -50%) scale(1);
    }
`;

export default function NextWaveCard({
    initialised,
    currentWave,
    nextWave,
    nextWaveIndex,
    onSkipWave,
    updatePathAtIndex,
    removePathAtIndex,
}: {
    initialised: boolean;
    currentWave: Wavepath | null;
    nextWave: Wavepath | null;
    nextWaveIndex: number;
    onSkipWave?: () => void;
    updatePathAtIndex: (index: number, newWave: Wavepath, showQueuingUIInline: boolean) => void;
    removePathAtIndex: (index: number, name: string) => void;
}): React.ReactElement {
    const [isEditorOpen, setEditorOpen] = useState(false);
    // the !!currentWave?.plan is based on the wave queue display logic in AutoGuide.tsx
    const isEditable = nextWave && !isPrePostLudePathScore(nextWave.pathScore) && !!currentWave?.plan;
    const onUpdateWave = (newWave: Wavepath) => {
        const showQueuingUIInline = true;
        updatePathAtIndex(nextWaveIndex, newWave, showQueuingUIInline);
    };

    const onRemoveWave = () => {
        removePathAtIndex(nextWaveIndex, nextWave?.pathScore.name ?? '');
    };

    const nextWaveStages = nextWave?.pathScore.stages ?? [];
    const nextWaveDirection = nextWave?.pathScore.direction ?? TherapeuticDirection.SOOTHE;
    const nextWaveIntensities = nextWaveStages.map((s) =>
        getIntensityForStageAndDirection({ stage: s.stage, direction: nextWaveDirection }),
    );
    const nextWaveMinIntensity = min(nextWaveIntensities) ?? 0;
    const nextWaveMaxIntensity = max(nextWaveIntensities) ?? 0;
    const hasIntensityInformation = nextWaveMinIntensity > 0;
    const isConstantIntensity = nextWaveMinIntensity === nextWaveMaxIntensity;

    const nextWaveDuration = nextWave && nextWave.plan ? getDuration(nextWave.plan) : '';
    const nextWaveDurationFormatted = nextWaveDuration && formatDurationSeconds(nextWaveDuration * 60_000);

    return (
        <>
            <NowAndNextCard
                initialised={initialised}
                wave={nextWave}
                progress={0}
                heading={<></>}
                body={
                    <>
                        {isEditable ? <EditButton onClick={() => setEditorOpen(true)}>Edit</EditButton> : <></>}
                        {onSkipWave && (
                            <SkipWaveButton onClick={onSkipWave}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="80"
                                    height="80"
                                    viewBox="0 0 60 61"
                                    fill="none"
                                >
                                    <g style={{ mixBlendMode: 'color-burn' }}>
                                        <circle cx="30" cy="30.5" r="28.5" stroke="#2B2D3F" strokeWidth="3" />
                                    </g>
                                    <g style={{ mixBlendMode: 'color-burn' }}>
                                        <path d="M37 30.5L19 40.5V20.5L37 30.5Z" fill="#2B2D3F" />
                                        <rect x="37" y="20.5" width="5" height="20" fill="#2B2D3F" />
                                    </g>
                                </svg>
                            </SkipWaveButton>
                        )}
                    </>
                }
                adjustments={
                    nextWave ? (
                        <>
                            <Adjustment>
                                <AdjustmentItem>
                                    <AdjustmentIcon
                                        width="15"
                                        height="15"
                                        viewBox="0 0 15 15"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M11.55 0H3.45C3.02996 0 2.81994 0 2.65951 0.0817451C2.51839 0.15365 2.40365 0.268386 2.33175 0.409507C2.25 0.569941 2.25 0.779961 2.25 1.2V2.75589C2.25 3.12277 2.25 3.30622 2.29145 3.47885C2.32819 3.6319 2.3888 3.77822 2.47104 3.91243C2.5638 4.0638 2.69352 4.19352 2.95294 4.45294L5.15147 6.65147C5.44848 6.94848 5.59699 7.09699 5.65263 7.26824C5.70158 7.41887 5.70158 7.58113 5.65263 7.73176C5.59699 7.90301 5.44848 8.05151 5.15147 8.34853L2.95294 10.5471C2.69352 10.8065 2.5638 10.9362 2.47104 11.0876C2.3888 11.2218 2.32819 11.3681 2.29145 11.5212C2.25 11.6938 2.25 11.8772 2.25 12.2441V13.8C2.25 14.22 2.25 14.4301 2.33175 14.5905C2.40365 14.7316 2.51839 14.8463 2.65951 14.9183C2.81994 15 3.02996 15 3.45 15H11.55C11.97 15 12.1801 15 12.3405 14.9183C12.4816 14.8463 12.5963 14.7316 12.6683 14.5905C12.75 14.4301 12.75 14.22 12.75 13.8V12.2441C12.75 11.8772 12.75 11.6938 12.7086 11.5212C12.6718 11.3681 12.6112 11.2218 12.529 11.0876C12.4362 10.9362 12.3065 10.8065 12.0471 10.5471L9.84853 8.34853C9.55151 8.05151 9.40301 7.90301 9.34737 7.73176C9.29842 7.58113 9.29842 7.41887 9.34737 7.26824C9.40301 7.09699 9.55151 6.94848 9.84853 6.65147L12.0471 4.45294C12.3065 4.19352 12.4362 4.0638 12.529 3.91243C12.6112 3.77822 12.6718 3.6319 12.7086 3.47885C12.75 3.30622 12.75 3.12277 12.75 2.75589V1.2C12.75 0.779961 12.75 0.569941 12.6683 0.409507C12.5963 0.268386 12.4816 0.15365 12.3405 0.0817451C12.1801 0 11.97 0 11.55 0Z"
                                            fill="url(#timerGrad)"
                                        />
                                        <defs>
                                            <linearGradient
                                                id="timerGrad"
                                                x1="7.5"
                                                y1="0"
                                                x2="7.5"
                                                y2="15"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stopColor="#2B2D3F" stopOpacity="0.2" />
                                                <stop offset="1" stopColor="#2B2D3F" />
                                            </linearGradient>
                                        </defs>
                                    </AdjustmentIcon>
                                    <AdjustmentLabel>{nextWaveDurationFormatted}</AdjustmentLabel>
                                </AdjustmentItem>
                            </Adjustment>
                            <AdjustmentsDivider />
                            <Adjustment>
                                <AdjustmentItem>
                                    <AdjustmentIcon
                                        width="15"
                                        height="16"
                                        viewBox="0 0 15 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle cx="7.5" cy="8" r="7.5" fill="#2B2D3F" />
                                    </AdjustmentIcon>
                                    <AdjustmentLabel>{nextWave?.pathScore.name ?? 'Emotionality'}</AdjustmentLabel>
                                </AdjustmentItem>
                            </Adjustment>
                            <AdjustmentsDivider />
                            <Adjustment>
                                <AdjustmentItem>
                                    <AdjustmentIcon
                                        width="15"
                                        height="16"
                                        viewBox="0 0 15 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <rect y="0.5" width="15" height="15" fill="url(#lineGrad)" />
                                        <defs>
                                            <linearGradient
                                                id="lineGrad"
                                                x1="7.5"
                                                y1="0.5"
                                                x2="7.5"
                                                y2="15.5"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stopColor="#2B2D3F" />
                                                <stop offset="1" stopColor="#2B2D3F" stopOpacity="0" />
                                            </linearGradient>
                                        </defs>
                                    </AdjustmentIcon>
                                    <AdjustmentLabel>
                                        Intensity{' '}
                                        {hasIntensityInformation
                                            ? isConstantIntensity
                                                ? nextWaveMinIntensity
                                                : `${nextWaveMinIntensity} - ${nextWaveMaxIntensity}`
                                            : ''}
                                    </AdjustmentLabel>
                                </AdjustmentItem>
                            </Adjustment>
                        </>
                    ) : (
                        <></>
                    )
                }
            />
            {isEditorOpen && nextWave && (
                <WaveCardDialog
                    isOpen={isEditorOpen}
                    wave={nextWave}
                    waveIndex={nextWaveIndex}
                    previousWave={currentWave ?? undefined}
                    shouldAllowAudioPreviews={false}
                    onSkipToWave={
                        onSkipWave &&
                        (() => {
                            onSkipWave?.();
                            setEditorOpen(false);
                        })
                    }
                    onUpdateWave={onUpdateWave}
                    onRemoveWave={onRemoveWave}
                    onClose={() => setEditorOpen(false)}
                />
            )}
        </>
    );
}
